import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import {
  Search,
  Plus,
  Filter,
  Download,
  Upload,
  Phone,
  Mail,
  Building2,
  Globe,
  MapPin,
  TrendingUp,
  Target,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  Calendar,
  ChevronRight,
  ChevronDown,
  Edit,
  Trash2,
  UserPlus,
  RefreshCw,
  Star,
  Tag,
  Ship,
  Package,
  ExternalLink,
  Linkedin,
  MoreVertical,
  Bot,
  Zap,
  Send,
  Play,
  Pause,
  Settings,
  ArrowRight,
  CheckSquare,
  Trophy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../context/AuthContext";
import { SmartLocationInput } from "../components/SmartLocationInput";
import { API_URL } from "../config/api";


// Status configuration
const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-700", icon: Plus },
  contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700", icon: Phone },
  engaged: { label: "Engaged", color: "bg-purple-100 text-purple-700", icon: Users },
  qualified: { label: "Qualified", color: "bg-green-100 text-green-700", icon: CheckCircle },
  converted: { label: "Converted", color: "bg-emerald-100 text-emerald-700", icon: Star },
  lost: { label: "Lost", color: "bg-red-100 text-red-700", icon: XCircle },
  archived: { label: "Archived", color: "bg-slate-100 text-slate-700", icon: Clock },
};

// Grade configuration
const GRADE_CONFIG = {
  A: { label: "A", color: "bg-green-500 text-white", description: "Hot Lead" },
  B: { label: "B", color: "bg-lime-500 text-white", description: "Warm Lead" },
  C: { label: "C", color: "bg-yellow-500 text-white", description: "Cool Lead" },
  D: { label: "D", color: "bg-orange-500 text-white", description: "Cold Lead" },
  F: { label: "F", color: "bg-slate-400 text-white", description: "Unqualified" },
};

// Industries
const INDUSTRIES = [
  "Manufacturing",
  "Distribution",
  "Retail",
  "Automotive",
  "Electronics",
  "Machinery",
  "Chemicals",
  "Food & Beverage",
  "Textiles",
  "Pharmaceuticals",
  "Construction",
  "Agriculture",
  "E-commerce",
  "Other",
];

// Trade lanes
const TRADE_LANES = [
  "Asia → CEE",
  "China → Slovenia",
  "China → Europe",
  "Far East → Europe",
  "India → Europe",
  "USA → Europe",
  "Europe → Asia",
  "Intra-Europe",
];

export default function SalesLeadsPage() {
  const { token, user } = useAuth();
  
  // State
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showNewLeadDialog, setShowNewLeadDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("leads");
  
  // Filters
  const [filters, setFilters] = useState({
    status: "",
    grade: "",
    search: "",
    country: "",
    minScore: "",
  });
  
  // New lead form
  const [newLead, setNewLead] = useState({
    company_name: "",
    vat_number: "",
    domain: "",
    website: "",
    industry: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact_position: "",
    contact_linkedin: "",
    estimated_fcl_per_year: "",
    estimated_lcl_per_year: "",
    primary_trade_lanes: [],
    current_forwarder: "",
    uses_port_koper: false,
    price_sensitivity: "",
    strategic_value: "",
    tags: [],
    notes: "",
  });

  // Activity form
  const [newActivity, setNewActivity] = useState({
    activity_type: "call",
    subject: "",
    description: "",
    outcome: "",
  });

  // Email drafts & templates state
  const [emailDrafts, setEmailDrafts] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  // My Tasks state
  const [myTasks, setMyTasks] = useState({ todays_tasks: [], overdue_tasks: [], total_pending: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Search parameters state (editable, not connected to backend yet)
  const [searchParams, setSearchParams] = useState({
    industries: ["Manufacturing", "Automotive", "Electronics", "Machinery", "Chemicals", "Pharmaceuticals", "Food & Beverage"],
    countries: ["Slovenia", "Croatia", "Austria", "Hungary", "Czech Republic", "Slovakia", "Poland", "Germany", "Italy"],
    keywords: ["import", "export", "logistics", "supply chain", "freight", "shipping", "warehouse"],
    companySize: { min: 10, max: 5000 },
    annualRevenue: { min: 1000000, max: 500000000 },
    excludeKeywords: ["broker", "agent", "consultant"],
    mustHaveWebsite: true,
    mustHaveEmail: true,
    preferPortKoper: true,
  });

  // Score calculation weights (editable, not connected to backend yet)
  const [scoreWeights, setScoreWeights] = useState({
    level1_market_fit: {
      industry_match: { weight: 25, description: "Industry matches target sectors" },
      geography_match: { weight: 25, description: "Located in target region (CEE)" },
      import_export_activity: { weight: 25, description: "Active in import/export" },
      company_size: { weight: 25, description: "Company size in target range" },
    },
    level2_freight_potential: {
      estimated_fcl_volume: { weight: 20, description: "Estimated FCL shipments/year" },
      estimated_lcl_volume: { weight: 20, description: "Estimated LCL shipments/year" },
      trade_lane_fit: { weight: 20, description: "Trade lanes match our routes" },
      warehousing_need: { weight: 20, description: "Needs warehousing services" },
      customs_activity: { weight: 20, description: "Requires customs clearance" },
    },
    level3_trigger_signals: {
      hiring_logistics: { weight: 25, description: "Hiring logistics staff" },
      new_warehouse: { weight: 25, description: "Opening new facility" },
      growth_funding: { weight: 25, description: "Recent funding/expansion" },
      switching_ports: { weight: 25, description: "Looking to change ports" },
    },
    grading: {
      A: { minScore: 200, label: "Hot Lead" },
      B: { minScore: 150, label: "Warm Lead" },
      C: { minScore: 100, label: "Potential" },
      D: { minScore: 50, label: "Low Priority" },
      F: { minScore: 0, label: "Not Qualified" },
    }
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.grade) params.append("grade", filters.grade);
      if (filters.search) params.append("search", filters.search);
      if (filters.country) params.append("country", filters.country);
      if (filters.minScore) params.append("min_score", filters.minScore);
      
      const response = await axios.get(`${API_URL}/sales-leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(response.data.leads);
      setStats(response.data.stats);
    } catch (error) {
      toast.error("Failed to fetch leads");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-leads/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    }
  }, [token]);

  // Fetch email drafts
  const fetchEmailDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      // Only fetch lead-related email drafts (not tracking credits or other types)
      const response = await axios.get(`${API_URL}/sales-leads/automation/email/drafts?lead_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailDrafts(response.data.drafts || []);
    } catch (error) {
      console.error("Failed to fetch email drafts:", error);
    } finally {
      setLoadingDrafts(false);
    }
  }, [token]);

  // Fetch email templates
  const fetchEmailTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-leads/automation/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch email templates:", error);
    }
  }, [token]);

  // Fetch my tasks
  const fetchMyTasks = useCallback(async () => {
    setLoadingTasks(true);
    try {
      const response = await axios.get(`${API_URL}/sales-leads/dashboard/my-tasks?user_id=${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyTasks(response.data);
    } catch (error) {
      console.error("Failed to fetch my tasks:", error);
    } finally {
      setLoadingTasks(false);
    }
  }, [token, user?.id]);

  // Fetch sales leaderboard
  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-leads/dashboard/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchLeads();
    fetchDashboardStats();
    fetchEmailDrafts();
    fetchEmailTemplates();
    fetchMyTasks();
    fetchLeaderboard();
  }, [fetchLeads, fetchDashboardStats, fetchEmailDrafts, fetchEmailTemplates, fetchMyTasks, fetchLeaderboard]);

  // Create lead
  const handleCreateLead = async () => {
    if (!newLead.company_name) {
      toast.error("Company name is required");
      return;
    }

    try {
      const payload = {
        ...newLead,
        estimated_fcl_per_year: newLead.estimated_fcl_per_year ? parseInt(newLead.estimated_fcl_per_year) : null,
        estimated_lcl_per_year: newLead.estimated_lcl_per_year ? parseInt(newLead.estimated_lcl_per_year) : null,
      };

      await axios.post(`${API_URL}/sales-leads`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Lead created successfully!");
      setShowNewLeadDialog(false);
      setNewLead({
        company_name: "",
        vat_number: "",
        domain: "",
        website: "",
        industry: "",
        address: "",
        city: "",
        country: "",
        postal_code: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        contact_position: "",
        contact_linkedin: "",
        estimated_fcl_per_year: "",
        estimated_lcl_per_year: "",
        primary_trade_lanes: [],
        current_forwarder: "",
        uses_port_koper: false,
        price_sensitivity: "",
        strategic_value: "",
        tags: [],
        notes: "",
      });
      fetchLeads();
      fetchDashboardStats();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error("Duplicate lead: " + error.response.data.detail);
      } else {
        toast.error("Failed to create lead");
      }
    }
  };

  // View lead details
  const handleViewLead = async (leadId) => {
    try {
      const response = await axios.get(`${API_URL}/sales-leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLead(response.data);
      setShowLeadDialog(true);
    } catch (error) {
      toast.error("Failed to load lead details");
    }
  };

  // Update lead status
  const handleStatusChange = async (leadId, newStatus, reason = null) => {
    try {
      await axios.post(
        `${API_URL}/sales-leads/${leadId}/status?new_status=${newStatus}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchLeads();
      if (selectedLead?.id === leadId) {
        handleViewLead(leadId);
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  // Assign lead
  const handleAssignLead = async (leadId, autoAssign = true) => {
    try {
      const response = await axios.post(
        `${API_URL}/sales-leads/${leadId}/assign?auto_assign=${autoAssign}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Lead assigned to ${response.data.assignee.full_name}`);
      fetchLeads();
      if (selectedLead?.id === leadId) {
        handleViewLead(leadId);
      }
    } catch (error) {
      toast.error("Failed to assign lead");
    }
  };

  // Log activity
  const handleLogActivity = async () => {
    if (!selectedLead || !newActivity.subject) {
      toast.error("Subject is required");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/sales-leads/${selectedLead.id}/activities`,
        newActivity,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Activity logged!");
      setNewActivity({ activity_type: "call", subject: "", description: "", outcome: "" });
      handleViewLead(selectedLead.id);
    } catch (error) {
      toast.error("Failed to log activity");
    }
  };

  // Complete task
  const handleCompleteTask = async (taskId) => {
    try {
      await axios.patch(
        `${API_URL}/sales-leads/${selectedLead.id}/tasks/${taskId}?status=completed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Task completed!");
      handleViewLead(selectedLead.id);
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  // Convert to customer
  const handleConvertToCustomer = async (leadId) => {
    try {
      const response = await axios.post(
        `${API_URL}/sales-leads/${leadId}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Lead converted to customer!");
      fetchLeads();
      setShowLeadDialog(false);
    } catch (error) {
      toast.error("Failed to convert lead");
    }
  };

  // Import CSV
  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API_URL}/sales-leads/import/csv`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );
      
      toast.success(`Imported ${response.data.imported} leads. ${response.data.duplicates} duplicates skipped.`);
      if (response.data.errors.length > 0) {
        console.error("Import errors:", response.data.errors);
      }
      setShowImportDialog(false);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to import CSV");
    }
  };

  // Render score badge
  const renderScoreBadge = (score) => {
    if (!score) return null;
    const config = GRADE_CONFIG[score.grade] || GRADE_CONFIG.F;
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${config.color}`}>
          {score.grade}
        </span>
        <span className="text-xs text-slate-500">{score.total_score} pts</span>
      </div>
    );
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Target className="h-6 w-6" />
                Sales Lead Engine
              </h1>
              <p className="text-blue-200 text-sm mt-1">
                AI-powered lead generation and qualification
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowImportDialog(true)}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
              <Button
                size="sm"
                onClick={() => setShowNewLeadDialog(true)}
                className="bg-white text-[#003366] hover:bg-blue-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Lead
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="leads" className="gap-2">
              <Users className="h-4 w-4" />
              Leads
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-2">
              <Mail className="h-4 w-4" />
              Email Drafts
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="automation" className="gap-2">
              <Bot className="h-4 w-4" />
              AI Scraper
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <Calendar className="h-4 w-4" />
              My Tasks
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-blue-700">{stats.by_status?.new || 0}</div>
                    <div className="text-xs text-blue-600">New Leads</div>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-yellow-700">{stats.by_status?.contacted || 0}</div>
                    <div className="text-xs text-yellow-600">Contacted</div>
                  </CardContent>
                </Card>
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-purple-700">{stats.by_status?.engaged || 0}</div>
                    <div className="text-xs text-purple-600">Engaged</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-green-700">{stats.by_status?.qualified || 0}</div>
                    <div className="text-xs text-green-600">Qualified</div>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-emerald-700">{stats.by_status?.converted || 0}</div>
                    <div className="text-xs text-emerald-600">Converted</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-4">
                    <div className="text-2xl font-bold text-slate-700">{stats.total || 0}</div>
                    <div className="text-xs text-slate-600">Total Leads</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search companies, contacts..."
                        value={filters.search}
                        onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.grade || "all"} onValueChange={(v) => setFilters(f => ({ ...f, grade: v === "all" ? "" : v }))}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Grade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Grades</SelectItem>
                      {Object.entries(GRADE_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{key} - {cfg.description}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchLeads}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Leads List */}
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                  </div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700">No leads found</h3>
                    <p className="text-slate-500 text-sm mt-1">Create your first lead or import from CSV</p>
                    <Button onClick={() => setShowNewLeadDialog(true)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Lead
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y">
                    {leads.map((lead) => (
                      <div
                        key={lead.id}
                        className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => handleViewLead(lead.id)}
                      >
                        <div className="flex items-start gap-4">
                          {/* Score Badge */}
                          <div className="flex-shrink-0 w-16 text-center">
                            {lead.score && (
                              <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold ${GRADE_CONFIG[lead.score.grade]?.color || 'bg-slate-400 text-white'}`}>
                                {lead.score.grade}
                              </div>
                            )}
                            <div className="text-xs text-slate-500 mt-1">{lead.score?.total_score || 0} pts</div>
                          </div>

                          {/* Company Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-slate-900">{lead.company_name}</h3>
                              {renderStatusBadge(lead.status)}
                              {lead.lead_temperature && (
                                <Badge className={`text-xs ${
                                  lead.lead_temperature === 'hot' ? 'bg-red-100 text-red-700' :
                                  lead.lead_temperature === 'warm' ? 'bg-orange-100 text-orange-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {lead.lead_temperature === 'hot' ? '🔥 Hot' :
                                   lead.lead_temperature === 'warm' ? '♨️ Warm' : '❄️ Cold'}
                                </Badge>
                              )}
                              {lead.uses_port_koper && (
                                <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
                                  <Ship className="h-3 w-3 mr-1" />
                                  Port Koper
                                </Badge>
                              )}
                              {lead.trigger_signals && Object.values(lead.trigger_signals).some(v => v) && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-600">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Trigger Signal
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                              {lead.industry && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="h-3 w-3" />
                                  {lead.industry}
                                </span>
                              )}
                              {lead.city && lead.country && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {lead.city}, {lead.country}
                                </span>
                              )}
                              {(lead.employee_count || lead.employee_range) && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {lead.employee_count || lead.employee_range} employees
                                </span>
                              )}
                              {lead.domain && (
                                <a
                                  href={lead.domain.startsWith('http') ? lead.domain : `https://${lead.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  <Globe className="h-3 w-3" />
                                  {lead.domain}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            {/* Contact */}
                            {lead.contact_name && (
                              <div className="flex items-center gap-3 mt-2 text-sm">
                                <span className="text-slate-700">{lead.contact_name}</span>
                                {lead.contact_email && (
                                  <span className="text-slate-500">{lead.contact_email}</span>
                                )}
                              </div>
                            )}
                            {/* Volume */}
                            {(lead.estimated_fcl_per_year || lead.estimated_lcl_per_year) && (
                              <div className="flex items-center gap-3 mt-2">
                                {lead.estimated_fcl_per_year > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    <Package className="h-3 w-3 mr-1" />
                                    {lead.estimated_fcl_per_year} FCL/yr
                                  </Badge>
                                )}
                                {lead.estimated_lcl_per_year > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    <Package className="h-3 w-3 mr-1" />
                                    {lead.estimated_lcl_per_year} LCL/yr
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {!lead.assigned_to && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssignLead(lead.id);
                                }}
                              >
                                <UserPlus className="h-4 w-4 mr-1" />
                                Assign
                              </Button>
                            )}
                            {lead.assigned_to && (
                              <div className="text-xs text-slate-500">
                                <span className="font-medium">{lead.assigned_to.full_name}</span>
                              </div>
                            )}
                            <ChevronRight className="h-5 w-5 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Queue Section - Shows planned/scheduled emails */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Mail className="h-5 w-5 text-blue-600" />
                      Email Queue
                    </CardTitle>
                    <CardDescription>
                      Scheduled and pending emails for leads
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchEmailDrafts}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {emailDrafts.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <Mail className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No scheduled emails</p>
                    <p className="text-xs mt-1">Start email sequences on leads to see them here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emailDrafts.slice(0, 10).map((draft) => (
                      <div key={draft.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            draft.status === 'pending_approval' ? 'bg-yellow-500' :
                            draft.status === 'sent' ? 'bg-green-500' :
                            draft.status === 'approved_not_sent' ? 'bg-blue-500' :
                            draft.status === 'rejected' ? 'bg-red-500' : 'bg-slate-400'
                          }`} />
                          <div>
                            <div className="font-medium text-sm">{draft.company_name}</div>
                            <div className="text-xs text-slate-500">
                              {draft.to_name} • {(draft.step || '').replace('_', ' ')}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={
                            draft.status === 'sent' ? 'default' :
                            draft.status === 'pending_approval' ? 'outline' :
                            draft.status === 'approved_not_sent' ? 'secondary' : 'destructive'
                          } className="text-xs">
                            {draft.status === 'pending_approval' ? 'Queued' :
                             draft.status === 'approved_not_sent' ? 'Ready' :
                             (draft.status || '').replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    {emailDrafts.length > 10 && (
                      <p className="text-xs text-slate-500 text-center pt-2">
                        +{emailDrafts.length - 10} more emails
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Search Parameters & Score Calculation Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Search Parameters */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5 text-blue-600" />
                    Lead Search Parameters
                  </CardTitle>
                  <CardDescription>
                    Configure criteria for AI lead scraping (preview only - changes not saved yet)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Target Industries */}
                  <div>
                    <Label className="text-sm font-medium">Target Industries</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {searchParams.industries.map((ind, idx) => (
                        <Badge key={idx} variant="outline" className="cursor-pointer hover:bg-blue-50">
                          {ind}
                          <button 
                            className="ml-1 text-slate-400 hover:text-red-500"
                            onClick={() => setSearchParams({
                              ...searchParams,
                              industries: searchParams.industries.filter((_, i) => i !== idx)
                            })}
                          >×</button>
                        </Badge>
                      ))}
                      <Input 
                        placeholder="Add industry..."
                        className="w-32 h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value) {
                            setSearchParams({
                              ...searchParams,
                              industries: [...searchParams.industries, e.target.value]
                            });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Target Countries */}
                  <div>
                    <Label className="text-sm font-medium">Target Countries</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {searchParams.countries.map((country, idx) => (
                        <Badge key={idx} variant="outline" className="cursor-pointer hover:bg-green-50">
                          {country}
                          <button 
                            className="ml-1 text-slate-400 hover:text-red-500"
                            onClick={() => setSearchParams({
                              ...searchParams,
                              countries: searchParams.countries.filter((_, i) => i !== idx)
                            })}
                          >×</button>
                        </Badge>
                      ))}
                      <Input 
                        placeholder="Add country..."
                        className="w-32 h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value) {
                            setSearchParams({
                              ...searchParams,
                              countries: [...searchParams.countries, e.target.value]
                            });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Keywords */}
                  <div>
                    <Label className="text-sm font-medium">Search Keywords</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {searchParams.keywords.map((kw, idx) => (
                        <Badge key={idx} variant="secondary" className="cursor-pointer">
                          {kw}
                          <button 
                            className="ml-1 text-slate-400 hover:text-red-500"
                            onClick={() => setSearchParams({
                              ...searchParams,
                              keywords: searchParams.keywords.filter((_, i) => i !== idx)
                            })}
                          >×</button>
                        </Badge>
                      ))}
                      <Input 
                        placeholder="Add keyword..."
                        className="w-32 h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value) {
                            setSearchParams({
                              ...searchParams,
                              keywords: [...searchParams.keywords, e.target.value]
                            });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Exclude Keywords */}
                  <div>
                    <Label className="text-sm font-medium">Exclude Keywords</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {searchParams.excludeKeywords.map((kw, idx) => (
                        <Badge key={idx} variant="destructive" className="cursor-pointer opacity-70">
                          {kw}
                          <button 
                            className="ml-1 hover:text-white"
                            onClick={() => setSearchParams({
                              ...searchParams,
                              excludeKeywords: searchParams.excludeKeywords.filter((_, i) => i !== idx)
                            })}
                          >×</button>
                        </Badge>
                      ))}
                      <Input 
                        placeholder="Add exclude..."
                        className="w-32 h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value) {
                            setSearchParams({
                              ...searchParams,
                              excludeKeywords: [...searchParams.excludeKeywords, e.target.value]
                            });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Company Size Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Min Employees</Label>
                      <Input 
                        type="number"
                        value={searchParams.companySize.min}
                        onChange={(e) => setSearchParams({
                          ...searchParams,
                          companySize: { ...searchParams.companySize, min: parseInt(e.target.value) || 0 }
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Max Employees</Label>
                      <Input 
                        type="number"
                        value={searchParams.companySize.max}
                        onChange={(e) => setSearchParams({
                          ...searchParams,
                          companySize: { ...searchParams.companySize, max: parseInt(e.target.value) || 0 }
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={searchParams.mustHaveWebsite}
                        onChange={(e) => setSearchParams({...searchParams, mustHaveWebsite: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">Must have website</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={searchParams.mustHaveEmail}
                        onChange={(e) => setSearchParams({...searchParams, mustHaveEmail: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">Must have contact email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={searchParams.preferPortKoper}
                        onChange={(e) => setSearchParams({...searchParams, preferPortKoper: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">Prefer companies near Port of Koper</span>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Score Calculation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Lead Scoring Weights
                  </CardTitle>
                  <CardDescription>
                    Configure how leads are scored and graded (preview only - changes not saved yet)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Level 1: Market Fit */}
                  <div>
                    <h4 className="font-medium text-sm text-blue-700 mb-2">Level 1: Market Fit (max 100 pts)</h4>
                    <div className="space-y-2">
                      {Object.entries(scoreWeights.level1_market_fit).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <Input 
                            type="number"
                            value={val.weight}
                            onChange={(e) => setScoreWeights({
                              ...scoreWeights,
                              level1_market_fit: {
                                ...scoreWeights.level1_market_fit,
                                [key]: { ...val, weight: parseInt(e.target.value) || 0 }
                              }
                            })}
                            className="w-16 h-8 text-center"
                          />
                          <span className="text-sm text-slate-600 flex-1">{val.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Level 2: Freight Potential */}
                  <div>
                    <h4 className="font-medium text-sm text-green-700 mb-2">Level 2: Freight Potential (max 100 pts)</h4>
                    <div className="space-y-2">
                      {Object.entries(scoreWeights.level2_freight_potential).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <Input 
                            type="number"
                            value={val.weight}
                            onChange={(e) => setScoreWeights({
                              ...scoreWeights,
                              level2_freight_potential: {
                                ...scoreWeights.level2_freight_potential,
                                [key]: { ...val, weight: parseInt(e.target.value) || 0 }
                              }
                            })}
                            className="w-16 h-8 text-center"
                          />
                          <span className="text-sm text-slate-600 flex-1">{val.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Level 3: Trigger Signals */}
                  <div>
                    <h4 className="font-medium text-sm text-orange-700 mb-2">Level 3: Trigger Signals (max 100 pts)</h4>
                    <div className="space-y-2">
                      {Object.entries(scoreWeights.level3_trigger_signals).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <Input 
                            type="number"
                            value={val.weight}
                            onChange={(e) => setScoreWeights({
                              ...scoreWeights,
                              level3_trigger_signals: {
                                ...scoreWeights.level3_trigger_signals,
                                [key]: { ...val, weight: parseInt(e.target.value) || 0 }
                              }
                            })}
                            className="w-16 h-8 text-center"
                          />
                          <span className="text-sm text-slate-600 flex-1">{val.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Grading Thresholds */}
                  <div>
                    <h4 className="font-medium text-sm text-slate-700 mb-2">Grade Thresholds</h4>
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(scoreWeights.grading).map(([grade, val]) => (
                        <div key={grade} className="text-center">
                          <div className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center text-white font-bold ${
                            grade === 'A' ? 'bg-green-500' :
                            grade === 'B' ? 'bg-blue-500' :
                            grade === 'C' ? 'bg-yellow-500' :
                            grade === 'D' ? 'bg-orange-500' : 'bg-red-500'
                          }`}>
                            {grade}
                          </div>
                          <Input 
                            type="number"
                            value={val.minScore}
                            onChange={(e) => setScoreWeights({
                              ...scoreWeights,
                              grading: {
                                ...scoreWeights.grading,
                                [grade]: { ...val, minScore: parseInt(e.target.value) || 0 }
                              }
                            })}
                            className="w-full h-7 text-center text-xs mt-1"
                          />
                          <span className="text-xs text-slate-500">{val.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                    <strong>Note:</strong> Changes here are for preview only. To activate new scoring, contact system administrator.
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Email Drafts Tab - Template Approval */}
          <TabsContent value="emails">
            {/* Workflow Approval Status */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      Email Sequence Workflow
                    </CardTitle>
                    <CardDescription>
                      Approve the entire email flow once - emails will be sent automatically
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={emailTemplates.some(t => t.approved) ? "default" : "outline"} className="text-sm py-1 px-3">
                      {emailTemplates.some(t => t.approved) ? "Workflow Approved" : "Pending Approval"}
                    </Badge>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={async () => {
                        try {
                          await axios.post(
                            `${API_URL}/sales-leads/automation/email/workflow/approve`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success('Email workflow approved! Emails will now be sent automatically.');
                          fetchEmailTemplates();
                        } catch (err) {
                          toast.error('Failed to approve workflow');
                        }
                      }}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Workflow
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-4 py-4">
                  {emailTemplates.map((template, idx) => (
                    <div key={template.step} className="flex items-center gap-4">
                      <div className={`p-4 rounded-lg border-2 text-center min-w-[160px] ${
                        template.approved ? 'border-green-500 bg-green-50' : 'border-slate-200'
                      }`}>
                        <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-sm font-bold ${
                          template.approved ? 'bg-green-500' : 'bg-blue-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="font-medium text-sm">
                          {template.step === 'intro' ? 'Intro Email' : 
                           template.step === 'followup_1' ? 'Follow-up 1' : 'Follow-up 2'}
                        </div>
                        <div className="text-xs text-slate-500">Day {template.day || (idx * 3)}</div>
                        {template.approved && (
                          <CheckCircle className="h-4 w-4 text-green-500 mx-auto mt-2" />
                        )}
                      </div>
                      {idx < emailTemplates.length - 1 && (
                        <ArrowRight className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  Once approved, emails will be sent automatically based on the schedule above
                </p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Email Templates */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    Email Templates
                  </CardTitle>
                  <CardDescription>
                    Click to view & edit templates
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {emailTemplates.map((template, idx) => (
                    <div 
                      key={template.step} 
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        editingTemplate?.step === template.step 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                      onClick={() => setEditingTemplate(template)}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold ${
                          template.approved ? 'bg-green-500' : 'bg-blue-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <span className="font-medium text-sm">
                          {template.step === 'intro' ? 'Intro Email' : 
                           template.step === 'followup_1' ? 'Follow-up 1' : 'Follow-up 2'}
                        </span>
                        <Badge variant="outline" className="ml-auto text-xs">
                          Day {template.day || (idx * 3)}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 truncate">{template.subject}</p>
                    </div>
                  ))}
                  
                  <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                    <p className="font-medium mb-1">Variables:</p>
                    <code>
                      {'{contact_name}'}, {'{company_name}'}, {'{industry}'}, {'{sender_name}'}
                    </code>
                  </div>
                </CardContent>
              </Card>

              {/* Template Editor */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>
                    {editingTemplate ? `Edit: ${editingTemplate.step === 'intro' ? 'Intro Email' : 
                      editingTemplate.step === 'followup_1' ? 'Follow-up 1' : 'Follow-up 2'}` : 'Select a template'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {editingTemplate ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Subject Line</Label>
                        <Input 
                          value={editingTemplate.subject}
                          onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Email Body (HTML)</Label>
                        <Textarea 
                          value={editingTemplate.body_html}
                          onChange={(e) => setEditingTemplate({...editingTemplate, body_html: e.target.value})}
                          rows={10}
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <Label className="mb-2 block">Preview (with sample data)</Label>
                        <div 
                          className="prose prose-sm max-w-none bg-white rounded border p-4"
                          dangerouslySetInnerHTML={{ 
                            __html: (editingTemplate.body_html || '')
                              .replace(/{contact_name}/g, 'John Smith')
                              .replace(/{company_name}/g, 'Test Company')
                              .replace(/{industry}/g, 'Manufacturing')
                              .replace(/{sender_name}/g, 'Rok Kobal')
                          }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={async () => {
                            try {
                              await axios.put(
                                `${API_URL}/sales-leads/automation/email/templates/${editingTemplate.step}`,
                                editingTemplate,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              toast.success('Template saved');
                              fetchEmailTemplates();
                            } catch (err) {
                              toast.error('Failed to save template');
                            }
                          }}
                        >
                          Save Template
                        </Button>
                        <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>Click on a template to preview & edit</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Email Activity Log */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-slate-600" />
                      Email Activity Log
                    </CardTitle>
                    <CardDescription>Recent emails sent and scheduled</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchEmailDrafts}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {emailDrafts.length === 0 ? (
                  <div className="text-center py-6 text-slate-500">
                    <Mail className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No email activity yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emailDrafts.map((draft) => (
                      <div key={draft.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {draft.status === 'sent' && <CheckCircle className="h-4 w-4 text-green-600" />}
                          {draft.status === 'pending_approval' && <Clock className="h-4 w-4 text-yellow-600" />}
                          {draft.status === 'approved_not_sent' && <Clock className="h-4 w-4 text-blue-600" />}
                          {draft.status === 'rejected' && <XCircle className="h-4 w-4 text-red-600" />}
                          {draft.status === 'error' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                          <div>
                            <span className="font-medium text-sm">{draft.company_name}</span>
                            <span className="text-slate-400 mx-2">•</span>
                            <span className="text-sm text-slate-600">{draft.to_name}</span>
                            <span className="text-slate-400 mx-2">•</span>
                            <span className="text-sm capitalize text-slate-500">{(draft.step || '').replace('_', ' ')}</span>
                          </div>
                        </div>
                        <Badge variant={
                          draft.status === 'sent' ? 'default' :
                          draft.status === 'pending_approval' ? 'outline' :
                          draft.status === 'rejected' ? 'destructive' : 'secondary'
                        }>
                          {(draft.status || '').replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {dashboardStats && (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Total Leads</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{dashboardStats.leads.total}</div>
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <span className="text-green-600">{dashboardStats.leads.qualified} qualified</span>
                        <span className="text-slate-400">|</span>
                        <span className="text-yellow-600">{dashboardStats.leads.stalled} stalled</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Activities This Month</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{dashboardStats.activities_this_month.total}</div>
                      <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
                        <span>{dashboardStats.activities_this_month.calls} calls</span>
                        <span>|</span>
                        <span>{dashboardStats.activities_this_month.meetings} meetings</span>
                        <span>|</span>
                        <span>{dashboardStats.activities_this_month.visits} visits</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Conversion Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{dashboardStats.conversions_this_month.rate}%</div>
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <span className="text-green-600">{dashboardStats.conversions_this_month.converted} won</span>
                        <span className="text-slate-400">|</span>
                        <span className="text-red-600">{dashboardStats.conversions_this_month.lost} lost</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-500">Pipeline Value</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">€{dashboardStats.pipeline.value.toLocaleString()}</div>
                      <div className="text-sm text-slate-600 mt-2">
                        {dashboardStats.pipeline.count} active deals
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Alerts */}
                {dashboardStats.tasks.overdue > 0 && (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                        <div>
                          <p className="font-medium text-orange-800">
                            {dashboardStats.tasks.overdue} overdue tasks
                          </p>
                          <p className="text-sm text-orange-600">
                            Please review and complete your pending tasks
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Grade Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>Lead Quality Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      {Object.entries(GRADE_CONFIG).map(([grade, config]) => (
                        <div key={grade} className="flex-1 text-center">
                          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${config.color}`}>
                            {grade}
                          </div>
                          <div className="text-2xl font-bold mt-2">{stats?.by_grade?.[grade] || 0}</div>
                          <div className="text-xs text-slate-500">{config.description}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* AI Automation Tab */}
          <TabsContent value="automation">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* AI Scraper */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-purple-600" />
                    AI Lead Scraper
                  </CardTitle>
                  <CardDescription>
                    AI-powered web scraping to discover potential customers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-medium text-purple-800 mb-2">Target Criteria</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-purple-600">Industries:</span>
                        <p className="text-slate-700">Manufacturing, Distribution, Retail</p>
                      </div>
                      <div>
                        <span className="text-purple-600">Countries:</span>
                        <p className="text-slate-700">Slovenia, Austria, Hungary, Croatia</p>
                      </div>
                      <div>
                        <span className="text-purple-600">Keywords:</span>
                        <p className="text-slate-700">import, logistics, shipping, freight</p>
                      </div>
                      <div>
                        <span className="text-purple-600">Min Score:</span>
                        <p className="text-slate-700">100 points (Grade C+)</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                      onClick={async () => {
                        try {
                          toast.info("Starting AI scrape job...");
                          const res = await axios.post(`${API_URL}/sales-leads/automation/scrape/manual`, {
                            sources: ["google"],
                            target_industries: ["manufacturing", "distribution", "retail"],
                            target_countries: ["Slovenia", "Austria", "Hungary", "Croatia"],
                            keywords: ["import", "logistics", "shipping", "freight"],
                            max_leads_per_run: 10,
                            min_score_threshold: 100
                          }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(`Scrape job started! Job ID: ${res.data.job_id}`);
                          fetchLeads();
                        } catch (err) {
                          toast.error("Failed to start scrape job");
                        }
                      }}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Start AI Scrape
                    </Button>
                    <Button variant="outline">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="text-xs text-slate-500">
                    Last run: Never | Schedule: Manual
                  </div>
                </CardContent>
              </Card>

              {/* Email Automation */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-600" />
                    Email Sequences
                  </CardTitle>
                  <CardDescription>
                    3-email drip campaign automation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2">Sequence Steps</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-orange-200 text-orange-700 flex items-center justify-center text-xs font-bold">1</div>
                        <span className="font-medium">Intro Email</span>
                        <span className="text-slate-500">- Day 0</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-orange-200 text-orange-700 flex items-center justify-center text-xs font-bold">2</div>
                        <span className="font-medium">Follow-up 1</span>
                        <span className="text-slate-500">- Day 3</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-6 h-6 rounded-full bg-orange-200 text-orange-700 flex items-center justify-center text-xs font-bold">3</div>
                        <span className="font-medium">Follow-up 2</span>
                        <span className="text-slate-500">- Day 6</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API_URL}/sales-leads/automation/process-sequences`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(`Processed ${res.data.processed} sequences, sent ${res.data.sent} emails`);
                        } catch (err) {
                          toast.error("Failed to process sequences");
                        }
                      }}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Process Sequences
                    </Button>
                  </div>
                  
                  <div className="text-xs text-slate-500">
                    Active sequences: 0 | Emails sent today: 0
                  </div>
                </CardContent>
              </Card>

              {/* Capacity Status */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Capacity Control</CardTitle>
                  <CardDescription>
                    System monitors lead backlog to prevent overflow
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">{stats?.by_status?.new || 0}</div>
                      <div className="text-xs text-slate-500">Unassigned Leads</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">1</div>
                      <div className="text-xs text-slate-500">Sales Team Size</div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">40</div>
                      <div className="text-xs text-slate-500">Weekly Capacity</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-700">Healthy</div>
                      <div className="text-xs text-green-600">System Status</div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Rule:</strong> Scraping will pause automatically when unassigned leads exceed 3x weekly sales capacity (120 leads).
                      This prevents lead overflow and ensures quality follow-up.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* My Tasks Section */}
              <div className="lg:col-span-2 space-y-4">
                {/* Overdue Tasks */}
                {myTasks.overdue_tasks?.length > 0 && (
                  <Card className="border-red-200 bg-red-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-red-700 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Overdue Tasks ({myTasks.overdue_tasks.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {myTasks.overdue_tasks.map((task) => (
                          <div 
                            key={task.id} 
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200"
                            data-testid={`overdue-task-${task.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-red-300"
                                onChange={() => {
                                  // Mark task complete for the lead
                                  const leadId = task.lead_id;
                                  axios.patch(
                                    `${API_URL}/sales-leads/${leadId}/tasks/${task.id}?status=completed`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` }}
                                  ).then(() => {
                                    toast.success("Task completed");
                                    fetchMyTasks();
                                  }).catch(() => toast.error("Failed to complete task"));
                                }}
                              />
                              <div>
                                <p className="font-medium">{task.title}</p>
                                <p className="text-sm text-slate-500">
                                  {task.lead?.company_name} • {task.lead?.contact_name}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant="destructive" className="mb-1">
                                {new Date(task.due_date).toLocaleDateString()}
                              </Badge>
                              {task.lead?.score?.total_score && (
                                <p className="text-xs text-slate-500">{task.lead.score.total_score} pts</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Today's Tasks */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CheckSquare className="h-5 w-5" />
                        Today's Tasks
                      </CardTitle>
                      <CardDescription>Prioritized by lead score</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchMyTasks} disabled={loadingTasks}>
                      <RefreshCw className={`h-4 w-4 ${loadingTasks ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {myTasks.todays_tasks?.length === 0 && myTasks.overdue_tasks?.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                        <p className="font-medium text-green-700">All caught up!</p>
                        <p className="text-sm">No tasks due today. Check the leads tab to follow up on opportunities.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {myTasks.todays_tasks?.map((task) => (
                          <div 
                            key={task.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                            data-testid={`today-task-${task.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300"
                                onChange={() => {
                                  const leadId = task.lead_id;
                                  axios.patch(
                                    `${API_URL}/sales-leads/${leadId}/tasks/${task.id}?status=completed`,
                                    {},
                                    { headers: { Authorization: `Bearer ${token}` }}
                                  ).then(() => {
                                    toast.success("Task completed");
                                    fetchMyTasks();
                                    fetchDashboardStats();
                                  }).catch(() => toast.error("Failed to complete task"));
                                }}
                              />
                              <div>
                                <p className="font-medium">{task.title}</p>
                                <p className="text-sm text-slate-500">
                                  {task.lead?.company_name} • {task.lead?.contact_name}
                                  {task.lead?.contact_phone && ` • ${task.lead.contact_phone}`}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline" className="mb-1">
                                Due: {new Date(task.due_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Badge>
                              {task.lead?.score?.total_score && (
                                <p className="text-xs text-slate-500">{task.lead.score.total_score} pts</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Leaderboard Section */}
              <div>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-yellow-500" />
                      Sales Leaderboard
                    </CardTitle>
                    <CardDescription>This month's performance</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {leaderboard.length === 0 ? (
                      <div className="text-center py-6 text-slate-500">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No activity recorded yet</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {leaderboard.slice(0, 5).map((entry, index) => (
                          <div 
                            key={entry.user?.id || index}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              index === 0 ? 'bg-yellow-50 border border-yellow-200' :
                              index === 1 ? 'bg-slate-100' :
                              index === 2 ? 'bg-orange-50' : 'bg-white border'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                index === 1 ? 'bg-slate-400 text-white' :
                                index === 2 ? 'bg-orange-400 text-white' :
                                'bg-slate-200 text-slate-600'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{entry.user?.full_name || 'Unknown'}</p>
                                <p className="text-xs text-slate-500">
                                  {entry.conversions} conversions • {entry.activities} activities
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{entry.score}</p>
                              <p className="text-xs text-slate-500">points</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* KPI Summary */}
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-sm font-medium mb-3">Scoring System</h4>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                        <div className="flex justify-between">
                          <span>Activity:</span>
                          <span className="font-medium">+1 pt</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Call:</span>
                          <span className="font-medium">+2 pts</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Meeting:</span>
                          <span className="font-medium">+5 pts</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Visit:</span>
                          <span className="font-medium">+10 pts</span>
                        </div>
                        <div className="flex justify-between col-span-2">
                          <span>Conversion:</span>
                          <span className="font-medium text-green-600">+20 pts</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Lead Dialog */}
      <Dialog open={showNewLeadDialog} onOpenChange={setShowNewLeadDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
            <DialogDescription>Enter company and contact information</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Company Info */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={newLead.company_name}
                    onChange={(e) => setNewLead(l => ({ ...l, company_name: e.target.value }))}
                    placeholder="e.g., ABC Manufacturing d.o.o."
                  />
                </div>
                <div>
                  <Label>VAT Number</Label>
                  <Input
                    value={newLead.vat_number}
                    onChange={(e) => setNewLead(l => ({ ...l, vat_number: e.target.value }))}
                    placeholder="e.g., SI12345678"
                  />
                </div>
                <div>
                  <Label>Domain</Label>
                  <Input
                    value={newLead.domain}
                    onChange={(e) => setNewLead(l => ({ ...l, domain: e.target.value }))}
                    placeholder="e.g., company.com"
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={newLead.website}
                    onChange={(e) => setNewLead(l => ({ ...l, website: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label>Industry</Label>
                  <Select value={newLead.industry} onValueChange={(v) => setNewLead(l => ({ ...l, industry: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={newLead.address}
                    onChange={(e) => setNewLead(l => ({ ...l, address: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="mb-1 block">Location</Label>
                  <SmartLocationInput
                    value={{
                      city: newLead.city,
                      country: newLead.country || "",
                      port_code: null,
                      port_name: null,
                    }}
                    onChange={(loc) => setNewLead(l => ({
                      ...l,
                      city: loc.city || "",
                      country: loc.country || "",
                    }))}
                    showSeaport={true}
                    showAirport={false}
                    placeholder="Search city or port..."
                    data-testid="lead-location"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Primary Contact
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newLead.contact_name}
                    onChange={(e) => setNewLead(l => ({ ...l, contact_name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input
                    value={newLead.contact_position}
                    onChange={(e) => setNewLead(l => ({ ...l, contact_position: e.target.value }))}
                    placeholder="e.g., Logistics Manager"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newLead.contact_email}
                    onChange={(e) => setNewLead(l => ({ ...l, contact_email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newLead.contact_phone}
                    onChange={(e) => setNewLead(l => ({ ...l, contact_phone: e.target.value }))}
                  />
                </div>
                <div className="col-span-2">
                  <Label>LinkedIn Profile</Label>
                  <Input
                    value={newLead.contact_linkedin}
                    onChange={(e) => setNewLead(l => ({ ...l, contact_linkedin: e.target.value }))}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>
            </div>

            {/* Freight Profile */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Ship className="h-4 w-4" />
                Freight Profile
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Est. FCL Volume/Year</Label>
                  <Input
                    type="number"
                    value={newLead.estimated_fcl_per_year}
                    onChange={(e) => setNewLead(l => ({ ...l, estimated_fcl_per_year: e.target.value }))}
                    placeholder="e.g., 50"
                  />
                </div>
                <div>
                  <Label>Est. LCL Volume/Year</Label>
                  <Input
                    type="number"
                    value={newLead.estimated_lcl_per_year}
                    onChange={(e) => setNewLead(l => ({ ...l, estimated_lcl_per_year: e.target.value }))}
                    placeholder="e.g., 100"
                  />
                </div>
                <div>
                  <Label>Current Forwarder</Label>
                  <Input
                    value={newLead.current_forwarder}
                    onChange={(e) => setNewLead(l => ({ ...l, current_forwarder: e.target.value }))}
                    placeholder="e.g., DHL, Kuehne+Nagel"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="uses_port_koper"
                    checked={newLead.uses_port_koper}
                    onChange={(e) => setNewLead(l => ({ ...l, uses_port_koper: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="uses_port_koper" className="cursor-pointer">
                    Uses Port of Koper
                  </Label>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead(l => ({ ...l, notes: e.target.value }))}
                rows={3}
                placeholder="Additional information about this lead..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLeadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateLead}>
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${GRADE_CONFIG[selectedLead.score?.grade]?.color || 'bg-slate-400 text-white'}`}>
                      {selectedLead.score?.grade || '?'}
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{selectedLead.company_name}</DialogTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {renderStatusBadge(selectedLead.status)}
                        <span className="text-sm text-slate-500">Score: {selectedLead.score?.total_score || 0}</span>
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        Actions
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!selectedLead.assigned_to && (
                        <DropdownMenuItem onClick={() => handleAssignLead(selectedLead.id)}>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assign Lead
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedLead.id, "contacted")}>
                        Mark as Contacted
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedLead.id, "engaged")}>
                        Mark as Engaged
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(selectedLead.id, "qualified")}>
                        Mark as Qualified
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {selectedLead.contact_email && !selectedLead.email_sequence?.active && (
                        <DropdownMenuItem 
                          onClick={async () => {
                            try {
                              await axios.post(`${API_URL}/sales-leads/automation/email/drafts/create/${selectedLead.id}`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success("Email draft created! Go to Email Drafts tab to review & approve.");
                              fetchEmailDrafts();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to create email draft");
                            }
                          }}
                          className="text-orange-600"
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Start Email Sequence
                        </DropdownMenuItem>
                      )}
                      {selectedLead.email_sequence?.active && (
                        <DropdownMenuItem 
                          onClick={async () => {
                            try {
                              await axios.post(`${API_URL}/sales-leads/automation/email/stop-sequence/${selectedLead.id}`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success("Email sequence stopped");
                              handleViewLead(selectedLead.id);
                            } catch (err) {
                              toast.error("Failed to stop sequence");
                            }
                          }}
                          className="text-slate-600"
                        >
                          <Pause className="h-4 w-4 mr-2" />
                          Stop Email Sequence
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {selectedLead.status === "qualified" && (
                        <DropdownMenuItem onClick={() => handleConvertToCustomer(selectedLead.id)} className="text-green-600">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Convert to Customer
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => {
                          const reason = prompt("Reason for losing this lead:");
                          if (reason) handleStatusChange(selectedLead.id, "lost", reason);
                        }}
                        className="text-red-600"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Mark as Lost
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-6 mt-4">
                {/* Main Info */}
                <div className="col-span-2 space-y-6">
                  {/* Company Details */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        Company Details
                        {selectedLead.lead_temperature && (
                          <Badge className={`ml-2 ${
                            selectedLead.lead_temperature === 'hot' ? 'bg-red-100 text-red-700' :
                            selectedLead.lead_temperature === 'warm' ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {selectedLead.lead_temperature?.charAt(0).toUpperCase() + selectedLead.lead_temperature?.slice(1)} Lead
                          </Badge>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Description */}
                      {selectedLead.description && (
                        <p className="text-sm text-slate-600 italic border-l-2 border-slate-200 pl-3">
                          {selectedLead.description}
                        </p>
                      )}
                      
                      {/* Company Stats Row */}
                      <div className="grid grid-cols-3 gap-3">
                        {(selectedLead.year_founded || selectedLead.employee_count || selectedLead.employee_range) && (
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <Users className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                            <p className="text-xs text-slate-500">Employees</p>
                            <p className="font-semibold text-sm">
                              {selectedLead.employee_count || selectedLead.employee_range || 'N/A'}
                            </p>
                          </div>
                        )}
                        {selectedLead.year_founded && (
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <Calendar className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                            <p className="text-xs text-slate-500">Founded</p>
                            <p className="font-semibold text-sm">{selectedLead.year_founded}</p>
                          </div>
                        )}
                        {(selectedLead.annual_revenue_eur || selectedLead.revenue_range) && (
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <TrendingUp className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                            <p className="text-xs text-slate-500">Revenue</p>
                            <p className="font-semibold text-sm">
                              {selectedLead.annual_revenue_eur 
                                ? `€${(selectedLead.annual_revenue_eur / 1000000).toFixed(1)}M`
                                : selectedLead.revenue_range || 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Basic Info Grid */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedLead.industry && (
                          <div>
                            <span className="text-slate-500">Industry:</span>
                            <span className="ml-2 font-medium">{selectedLead.industry}</span>
                          </div>
                        )}
                        {selectedLead.domain && (
                          <div className="flex items-center">
                            <span className="text-slate-500">Website:</span>
                            <a
                              href={selectedLead.domain.startsWith('http') ? selectedLead.domain : `https://${selectedLead.domain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            >
                              {selectedLead.domain}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}
                        {selectedLead.city && selectedLead.country && (
                          <div>
                            <span className="text-slate-500">Location:</span>
                            <span className="ml-2 font-medium">{selectedLead.city}, {selectedLead.country}</span>
                          </div>
                        )}
                        {selectedLead.vat_number && (
                          <div>
                            <span className="text-slate-500">VAT:</span>
                            <span className="ml-2 font-medium">{selectedLead.vat_number}</span>
                          </div>
                        )}
                        {selectedLead.linkedin_url && (
                          <div className="flex items-center">
                            <span className="text-slate-500">LinkedIn:</span>
                            <a
                              href={selectedLead.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            >
                              <Linkedin className="h-3 w-3" />
                              Company Page
                            </a>
                          </div>
                        )}
                        {selectedLead.current_forwarder && (
                          <div>
                            <span className="text-slate-500">Current Forwarder:</span>
                            <span className="ml-2 font-medium">{selectedLead.current_forwarder}</span>
                          </div>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap gap-2">
                        {selectedLead.uses_port_koper && (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Ship className="h-3 w-3 mr-1" />
                            Uses Port of Koper
                          </Badge>
                        )}
                        {selectedLead.lead_type && (
                          <Badge variant="outline" className="text-slate-600">
                            {selectedLead.lead_type === 'importer' ? 'Importer' :
                             selectedLead.lead_type === 'exporter' ? 'Exporter' :
                             selectedLead.lead_type === 'both' ? 'Importer & Exporter' :
                             selectedLead.lead_type}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Trigger Signals */}
                  {selectedLead.trigger_signals && Object.values(selectedLead.trigger_signals).some(v => v) && (
                    <Card className="border-amber-200 bg-amber-50/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Zap className="h-4 w-4 text-amber-500" />
                          Trigger Signals Detected
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {selectedLead.trigger_signals.hiring_logistics && (
                            <Badge className="bg-green-100 text-green-700">
                              <UserPlus className="h-3 w-3 mr-1" />
                              Hiring Logistics Staff
                            </Badge>
                          )}
                          {selectedLead.trigger_signals.new_warehouse && (
                            <Badge className="bg-purple-100 text-purple-700">
                              <Building2 className="h-3 w-3 mr-1" />
                              New Warehouse
                            </Badge>
                          )}
                          {selectedLead.trigger_signals.growth_funding && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <TrendingUp className="h-3 w-3 mr-1" />
                              Growth/Funding
                            </Badge>
                          )}
                          {selectedLead.trigger_signals.switching_ports && (
                            <Badge className="bg-blue-100 text-blue-700">
                              <Ship className="h-3 w-3 mr-1" />
                              Switching Ports
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Primary Contact */}
                  {selectedLead.contact_name && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Primary Contact</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-start gap-4">
                          <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                            <Users className="h-5 w-5 text-slate-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{selectedLead.contact_name}</p>
                            {(selectedLead.contact_position || selectedLead.contact_department) && (
                              <p className="text-sm text-slate-500">
                                {selectedLead.contact_position}
                                {selectedLead.contact_department && ` - ${selectedLead.contact_department}`}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              {selectedLead.contact_email && (
                                <a href={`mailto:${selectedLead.contact_email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {selectedLead.contact_email}
                                </a>
                              )}
                              {selectedLead.contact_phone && (
                                <a href={`tel:${selectedLead.contact_phone}`} className="text-sm text-slate-600 flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {selectedLead.contact_phone}
                                </a>
                              )}
                              {selectedLead.contact_mobile && selectedLead.contact_mobile !== selectedLead.contact_phone && (
                                <a href={`tel:${selectedLead.contact_mobile}`} className="text-sm text-slate-600 flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {selectedLead.contact_mobile} (mobile)
                                </a>
                              )}
                              {selectedLead.contact_linkedin && (
                                <a href={selectedLead.contact_linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 flex items-center gap-1">
                                  <Linkedin className="h-3 w-3" />
                                  LinkedIn
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Additional Contacts */}
                  {selectedLead.contacts && selectedLead.contacts.length > 1 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Additional Contacts ({selectedLead.contacts.length - 1})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="max-h-[200px]">
                          <div className="space-y-3">
                            {selectedLead.contacts.slice(1).map((contact, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-600">
                                  {contact.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0,2) || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{contact.name || 'Unknown'}</p>
                                  {(contact.position || contact.department) && (
                                    <p className="text-xs text-slate-500 truncate">
                                      {contact.position}{contact.department && ` - ${contact.department}`}
                                    </p>
                                  )}
                                  <div className="flex flex-wrap items-center gap-2 mt-1">
                                    {contact.email && (
                                      <a href={`mailto:${contact.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                        <Mail className="h-3 w-3" />
                                        {contact.email}
                                      </a>
                                    )}
                                    {contact.phone && (
                                      <a href={`tel:${contact.phone}`} className="text-xs text-slate-500 flex items-center gap-1">
                                        <Phone className="h-3 w-3" />
                                        {contact.phone}
                                      </a>
                                    )}
                                    {contact.linkedin && (
                                      <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600">
                                        <Linkedin className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Log Activity */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Log Activity</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3">
                        <Select value={newActivity.activity_type} onValueChange={(v) => setNewActivity(a => ({ ...a, activity_type: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="meeting">Meeting</SelectItem>
                            <SelectItem value="linkedin">LinkedIn</SelectItem>
                            <SelectItem value="visit">On-site Visit</SelectItem>
                            <SelectItem value="note">Note</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={newActivity.outcome} onValueChange={(v) => setNewActivity(a => ({ ...a, outcome: v }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Outcome" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="neutral">Neutral</SelectItem>
                            <SelectItem value="negative">Negative</SelectItem>
                            <SelectItem value="no_answer">No Answer</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="col-span-2">
                          <Input
                            placeholder="Subject"
                            value={newActivity.subject}
                            onChange={(e) => setNewActivity(a => ({ ...a, subject: e.target.value }))}
                          />
                        </div>
                        <div className="col-span-2">
                          <Textarea
                            placeholder="Description..."
                            value={newActivity.description}
                            onChange={(e) => setNewActivity(a => ({ ...a, description: e.target.value }))}
                            rows={2}
                          />
                        </div>
                        <div className="col-span-2">
                          <Button onClick={handleLogActivity} className="w-full">
                            Log Activity
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Activity History */}
                  {selectedLead.activities?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Activity History</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[200px]">
                          <div className="space-y-3">
                            {selectedLead.activities.map((activity) => (
                              <div key={activity.id} className="flex gap-3 text-sm">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                  {activity.activity_type === 'call' && <Phone className="h-4 w-4" />}
                                  {activity.activity_type === 'email' && <Mail className="h-4 w-4" />}
                                  {activity.activity_type === 'meeting' && <Users className="h-4 w-4" />}
                                  {activity.activity_type === 'visit' && <MapPin className="h-4 w-4" />}
                                  {activity.activity_type === 'note' && <Edit className="h-4 w-4" />}
                                  {activity.activity_type === 'status_change' && <RefreshCw className="h-4 w-4" />}
                                </div>
                                <div>
                                  <p className="font-medium">{activity.subject}</p>
                                  {activity.description && (
                                    <p className="text-slate-500">{activity.description}</p>
                                  )}
                                  <p className="text-xs text-slate-400 mt-1">
                                    {new Date(activity.created_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  {/* Score Breakdown */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Lead Score Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Market Fit</span>
                          <span>{selectedLead.score?.level1_market_fit || 0}/100</span>
                        </div>
                        <Progress value={selectedLead.score?.level1_market_fit || 0} className="h-2" />
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Freight Potential</span>
                          <span>{selectedLead.score?.level2_freight_potential || 0}/100</span>
                        </div>
                        <Progress value={selectedLead.score?.level2_freight_potential || 0} className="h-2" />
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Trigger Signals</span>
                          <span>{selectedLead.score?.level3_trigger_signals || 0}/100</span>
                        </div>
                        <Progress value={selectedLead.score?.level3_trigger_signals || 0} className="h-2" />
                      </div>
                      <Separator />
                      <div className="flex justify-between font-medium">
                        <span>Total Score</span>
                        <span>{selectedLead.score?.total_score || 0}/300</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Freight Volume */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Estimated Volume</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {selectedLead.estimated_fcl_per_year > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">FCL/Year</span>
                          <span className="font-medium">{selectedLead.estimated_fcl_per_year}</span>
                        </div>
                      )}
                      {selectedLead.estimated_lcl_per_year > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">LCL/Year</span>
                          <span className="font-medium">{selectedLead.estimated_lcl_per_year}</span>
                        </div>
                      )}
                      {selectedLead.estimated_annual_teu > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-600">Annual TEU</span>
                          <span className="font-medium">{selectedLead.estimated_annual_teu}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tasks */}
                  {selectedLead.tasks?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Tasks</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedLead.tasks.filter(t => t.status === 'pending').slice(0, 5).map((task) => (
                            <div key={task.id} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                onChange={() => handleCompleteTask(task.id)}
                                className="h-4 w-4"
                              />
                              <span className={new Date(task.due_date) < new Date() ? 'text-red-600' : ''}>
                                {task.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Assignment */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Assignment</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedLead.assigned_to ? (
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{selectedLead.assigned_to.full_name}</p>
                            <p className="text-xs text-slate-500">{selectedLead.assigned_to.email}</p>
                          </div>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => handleAssignLead(selectedLead.id)}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assign Lead
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Leads from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with lead data. Required column: company_name
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <span className="text-blue-600 hover:underline">Click to upload</span>
                <span className="text-slate-500"> or drag and drop</span>
              </label>
              <p className="text-xs text-slate-400 mt-2">CSV files only</p>
            </div>
            <div className="text-xs text-slate-500">
              <p className="font-medium mb-1">Expected columns:</p>
              <p>company_name, vat_number, domain, website, industry, city, country, contact_name, contact_email, contact_phone, fcl_volume, lcl_volume</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
